import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip, CircularProgress } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import { loadNetworkInsight } from '../api/admin';

interface MachineIpDetailsDialogProps {
    open: boolean;
    onClose: () => void;
    machine: any; // Contains: serverId, serverName, ipAddress, licenseKey, lastSeenAt
    onSuccess?: () => void;
}

export function MachineIpDetailsDialog({ open, onClose, machine, onSuccess }: MachineIpDetailsDialogProps) {
    const [insight, setInsight] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [cooldownRemaining, setCooldownRemaining] = useState<number | null>(null);
    const [copyIpSuccess, setCopyIpSuccess] = useState(false);
    const [copyAsnSuccess, setCopyAsnSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (open && machine) {
            setInsight(null);
            setErrorMessage('');
            setCooldownRemaining(null);
            loadData(false);
        }
    }, [open, machine]);

    // Cooldown countdown timer
    useEffect(() => {
        if (cooldownRemaining === null || cooldownRemaining <= 0) return;
        const interval = setInterval(() => {
            setCooldownRemaining((prev) => {
                if (prev === null || prev <= 1) {
                    clearInterval(interval);
                    return null;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [cooldownRemaining]);

    const loadData = async (forceRefresh = false) => {
        if (!machine) return;
        setIsLoading(true);
        setErrorMessage('');
        try {
            const machineId = machine.serverId || machine.machineId;
            const ipAddress = machine.ipAddress;
            const machineName = machine.serverName || machine.machineName || 'Unknown';
            const licenseKey = machine.licenseKey;

            const data = await loadNetworkInsight({
                machineId,
                ipAddress,
                machineName,
                licenseKey,
                forceRefresh
            });
            setInsight(data);
            setCooldownRemaining(null);
            if (forceRefresh && onSuccess) {
                onSuccess();
            }
        } catch (err: any) {
            console.error('Error loading machine network insights:', err);
            const errData = err.response?.data;
            if (err.response?.status === 429 && errData?.error === 'throttled') {
                const nextAllowed = errData.nextAllowed;
                if (nextAllowed) {
                    const remainingSeconds = Math.max(0, Math.ceil(nextAllowed - Date.now() / 1000));
                    setCooldownRemaining(remainingSeconds);
                } else {
                    setCooldownRemaining(300); // 5 minutes default fallback
                }
            } else {
                setErrorMessage(errData?.message || 'Failed to load network insights.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopyIp = () => {
        const ip = machine?.ipAddress || insight?.currentIp;
        if (!ip) return;
        navigator.clipboard.writeText(ip);
        setCopyIpSuccess(true);
        setTimeout(() => setCopyIpSuccess(false), 2000);
    };

    const handleCopyAsn = () => {
        const asn = insight?.network?.asn;
        if (!asn || asn === '-') return;
        navigator.clipboard.writeText(asn);
        setCopyAsnSuccess(true);
        setTimeout(() => setCopyAsnSuccess(false), 2000);
    };

    const formatLastSeen = (timestampMs?: number) => {
        if (!timestampMs) return '-';
        const seconds = Math.floor((Date.now() - timestampMs) / 1000);
        if (seconds < 0 || seconds < 60) return 'Just now';
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        if (days === 1) return 'Yesterday';
        return `${days} days ago`;
    };

    const formatLastUpdated = (timestampSec?: number) => {
        if (!timestampSec) return '-';
        const seconds = Math.floor(Date.now() / 1000 - timestampSec);
        if (seconds < 0 || seconds < 60) return 'Just now';
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        if (days === 1) return 'Yesterday';
        return `${days} days ago`;
    };

    // Determine status badge
    const getNetworkStatus = () => {
        if (!insight) return null;
        if (insight.network?.type === 'private_network') {
            return {
                label: 'Private Network',
                className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
            };
        }
        // Check if lastIpChangedAt is within the last 7 days
        const sevenDaysInSeconds = 7 * 24 * 60 * 60;
        const lastIpChangedAt = insight.lastIpChangedAt || 0;
        const isRecentlyChanged = Math.floor(Date.now() / 1000) - lastIpChangedAt < sevenDaysInSeconds;

        if (isRecentlyChanged) {
            return {
                label: 'Recently Changed',
                className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200'
            };
        }
        return {
            label: 'Known Network',
            className: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
        };
    };

    const statusBadge = getNetworkStatus();
    const isPrivate = insight?.network?.type === 'private_network';

    return (
        <Dialog
            open={open}
            onClose={isLoading ? undefined : onClose}
            PaperProps={{
                className: 'w-[450px] max-w-full !rounded-3xl dark:bg-gray-800 dark:text-white p-2'
            }}
        >
            <DialogTitle className="flex justify-between items-center !font-bold !text-lg text-gray-900 dark:text-white">
                <span>Machine IP Details</span>
                <button
                    onClick={onClose}
                    disabled={isLoading}
                    className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg text-slate-500 cursor-pointer disabled:opacity-50"
                >
                    <CloseIcon className="w-5 h-5" />
                </button>
            </DialogTitle>

            <DialogContent className="space-y-4 !py-2">
                {/* Loader */}
                {isLoading && !insight && (
                    <div className="flex flex-col items-center py-12 space-y-3">
                        <CircularProgress size={36} className="text-primary-600" />
                        <span className="text-xs text-gray-400">Fetching network details...</span>
                    </div>
                )}

                {/* Error message */}
                {errorMessage && (
                    <div className="bg-red-50 dark:bg-red-950/20 text-red-600 dark:text-red-400 p-3 rounded-2xl text-xs font-semibold text-center border border-red-100 dark:border-red-900/40">
                        {errorMessage}
                    </div>
                )}

                {/* Cooldown Throttle message */}
                {cooldownRemaining !== null && (
                    <div className="bg-amber-50 dark:bg-amber-955/20 text-amber-700 dark:text-amber-400 p-3.5 rounded-2xl text-xs font-semibold border border-amber-100 dark:border-amber-900/30">
                        <p>Network data was refreshed recently.</p>
                        <p className="mt-0.5 text-gray-400 font-normal">
                            Try again in {Math.floor(cooldownRemaining / 60)}m {cooldownRemaining % 60}s.
                        </p>
                    </div>
                )}

                {/* Content */}
                {(insight || machine) && (
                    <div className="space-y-4">
                        {/* Network Status Badge */}
                        {statusBadge && (
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Status:</span>
                                <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${statusBadge.className}`}>
                                    {statusBadge.label}
                                </span>
                            </div>
                        )}

                        {/* Machine Information */}
                        <div className="bg-slate-50 dark:bg-slate-900/40 p-3.5 rounded-2xl border dark:border-gray-700/60 space-y-2">
                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Machine Information</h4>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="col-span-1 text-gray-400 font-medium">Name</div>
                                <div className="col-span-2 text-gray-800 dark:text-gray-250 font-semibold truncate">
                                    {machine?.serverName || insight?.machineName || 'Unknown'}
                                </div>

                                <div className="col-span-1 text-gray-400 font-medium">Machine ID</div>
                                <div className="col-span-2 text-gray-800 dark:text-gray-250 font-mono select-all truncate">
                                    {machine?.serverId || insight?.machineId || '-'}
                                </div>

                                <div className="col-span-1 text-gray-400 font-medium">License Key</div>
                                <div className="col-span-2 text-gray-800 dark:text-gray-250 font-mono select-all truncate">
                                    {machine?.licenseKey || insight?.licenseKey || '-'}
                                </div>
                            </div>
                        </div>

                        {/* IP Information */}
                        <div className="bg-slate-50 dark:bg-slate-900/40 p-3.5 rounded-2xl border dark:border-gray-700/60 space-y-2">
                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">IP Information</h4>
                            <div className="grid grid-cols-3 gap-2 text-xs items-center">
                                <div className="col-span-1 text-gray-400 font-medium">Public IP</div>
                                <div className="col-span-2 text-gray-800 dark:text-gray-250 font-mono font-bold break-all flex items-center gap-1.5">
                                    <span>{machine?.ipAddress || insight?.currentIp || 'N/A'}</span>
                                </div>

                                <div className="col-span-1 text-gray-400 font-medium">ISP</div>
                                <div className="col-span-2 text-gray-800 dark:text-gray-250 font-medium">
                                    {isPrivate ? (
                                        <span className="font-semibold text-blue-600 dark:text-blue-400">Private Network</span>
                                    ) : (
                                        insight?.network?.isp || <span className="text-gray-400 italic">No network data</span>
                                    )}
                                </div>

                                <div className="col-span-1 text-gray-400 font-medium">ASN</div>
                                <div className="col-span-2 text-gray-800 dark:text-gray-250 font-mono">
                                    {isPrivate ? '-' : (insight?.network?.asn || '-')}
                                </div>

                                <div className="col-span-1 text-gray-400 font-medium">Domain</div>
                                <div className="col-span-2 text-gray-800 dark:text-gray-250">
                                    {isPrivate ? '-' : (insight?.network?.domain || '-')}
                                </div>

                                <div className="col-span-1 text-gray-400 font-medium">Country</div>
                                <div className="col-span-2 text-gray-800 dark:text-gray-250">
                                    {isPrivate ? '-' : (insight?.network?.country || '-')}
                                </div>
                            </div>
                        </div>

                        {/* Metadata */}
                        <div className="bg-slate-50 dark:bg-slate-900/40 p-3.5 rounded-2xl border dark:border-gray-700/60 space-y-2">
                            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Metadata</h4>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="col-span-1 text-gray-400 font-medium">Last Seen</div>
                                <div className="col-span-2 text-gray-800 dark:text-gray-250 font-medium">
                                    {formatLastSeen(machine?.lastSeenAt || machine?.lastHeartbeat)}
                                </div>

                                <div className="col-span-1 text-gray-400 font-medium">Last Updated</div>
                                <div className="col-span-2 text-gray-800 dark:text-gray-250 font-medium">
                                    {formatLastUpdated(insight?.lastCheckedAt)}
                                </div>

                                <div className="col-span-1 text-gray-400 font-medium">IP Changed</div>
                                <div className="col-span-2 text-gray-800 dark:text-gray-250 font-medium">
                                    {insight?.lastIpChangedAt 
                                        ? formatLastUpdated(insight.lastIpChangedAt)
                                        : 'Never'
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </DialogContent>

            <DialogActions className="!px-6 !pb-4 flex flex-wrap justify-between gap-2">
                <div className="flex gap-2">
                    <Button
                        onClick={handleCopyIp}
                        disabled={isLoading || !(machine?.ipAddress || insight?.currentIp)}
                        className="!text-xs hover:!bg-slate-100"
                        startIcon={<ContentCopyIcon className="!w-3.5 !h-3.5" />}
                    >
                        {copyIpSuccess ? 'Copied!' : 'Copy IP'}
                    </Button>

                    <Button
                        onClick={handleCopyAsn}
                        disabled={isLoading || isPrivate || !insight?.network?.asn || insight?.network?.asn === '-'}
                        className="!text-xs hover:!bg-slate-100"
                        startIcon={<ContentCopyIcon className="!w-3.5 !h-3.5" />}
                    >
                        {copyAsnSuccess ? 'Copied!' : 'Copy ASN'}
                    </Button>
                </div>

                <div className="flex gap-2">
                    <Button
                        onClick={() => loadData(true)}
                        disabled={isLoading || cooldownRemaining !== null}
                        variant="outlined"
                        className="!rounded-xl !px-3 !py-1.5 !text-xs"
                        startIcon={<RefreshIcon className="!w-3.5 !h-3.5" />}
                    >
                        {isLoading ? 'Refreshing...' : 'Refresh'}
                    </Button>

                    <Button
                        onClick={onClose}
                        variant="contained"
                        color="inherit"
                        className="!rounded-xl !px-4 !py-1.5 !text-xs !bg-slate-100 hover:!bg-slate-200 dark:!bg-gray-700 dark:hover:!bg-gray-600 dark:!text-white"
                    >
                        Close
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    );
}
